import { shouldFilterFlareError } from "@/utils/monitoring/flareErrorFilter";
import type { Attributes } from "@flareapp/js";
import { safelyReportToFlare } from "./monitoring/safelyReportToFlare";

type FlareReporter = {
  report: (error: Error, context?: Attributes) => void | Promise<void>;
};

type FlareWindow = Window & {
  flare?: FlareReporter;
};

const asError = (error: unknown) => {
  if (isErrorLike(error)) {
    return error;
  }

  return new Error(String(error));
};

const isErrorLike = (error: unknown): error is Error =>
  typeof error === "object" &&
  error !== null &&
  "name" in error &&
  "message" in error &&
  typeof (error as { message?: unknown }).message === "string";

/**
 * client-side Flare reporter. Server-side errors need their own logger because
 * Flare's browser client is only available on window.
 */
export const reportToFlare = (
  error: unknown,
  context: string,
  metadata?: Record<string, unknown>,
) => {
  const normalizedError = asError(error);

  if (
    shouldFilterFlareError(normalizedError, undefined, {
      filterExpectedRequestErrors: true,
      filterTransientBrowserErrors: true,
    })
  ) {
    return;
  }

  if (typeof window === "undefined") {
    console.error(`[server] ${context}`, normalizedError, metadata ?? {});
    return;
  }

  const flare = (window as FlareWindow).flare;

  if (!flare) {
    console.error(`[client] ${context}`, normalizedError, metadata ?? {});
    return;
  }

  safelyReportToFlare(flare, normalizedError, {
    context,
    ...metadata,
  });
};
